import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { isRawDiarizationCode, Speaker } from 'api/speaker';
import Delta from 'quill-delta';
import { TransparencySchema } from 'api/settings/user-settings';
import { parseTrsx } from '@newtontechnologies/beey-trsx-js';
import DocumentExporter from './document-exporter';
import TextMetadata from './text-metadata';
import { defaultCaptionParameters } from './captions';
import { CAPTION_END_SYMBOL, formatTimeStampMarker, TIME_ANCHOR_SYMBOL, } from './text-utils';
dayjs.extend(duration);
export default class TrsxManager {
    constructor(editorController, creatorUserName) {
        this.importFromTime = 0;
        this.unmaintainedMetadata = [];
        this.currentEndIndex = 0;
        this.lastSpeakerId = null;
        this.isLastParagraphTerminated = true;
        this.etag = null;
        this.tryAddToDocumentSpeakers = (speaker) => {
            const { speakers } = this.editorController;
            const matchingDocumentSpeaker = speakers.getSpeakerById(speaker.id);
            if (matchingDocumentSpeaker === null) {
                speakers.addDocumentSpeaker(speaker);
            }
        };
        this.shouldInsertSpeaker = (paragraph) => {
            let insertSpeaker = true;
            if (paragraph.speakerId === this.lastSpeakerId) {
                insertSpeaker = false;
            }
            if (paragraph.display === 'withSpeaker') {
                insertSpeaker = true;
            }
            if (!this.isLastParagraphTerminated) {
                // NOTE: This means that the paragraph was already imported partially from
                // partial trsx.
                insertSpeaker = false;
                this.isLastParagraphTerminated = true;
            }
            return insertSpeaker;
        };
        this.changeToTransparency = (value, defaulValue) => (TransparencySchema.values().default(defaulValue).sanitize(Number(value)));
        this.editorController = editorController;
        this.creatorUserName = creatorUserName;
        this.trsxSpeakers = {};
        this.captionMetadata = null;
        this.lastEditorUsername = null;
        this.lastPlaybackTime = null;
        this.trsxCreatedTime = null;
    }
    export() {
        return new DocumentExporter(this.editorController, this.editorController.textMetadata, this.editorController.speakers, this.unmaintainedMetadata, this.creatorUserName, this.editorController.lastHandledPhraseTime, this.editorController.lastHandledMessageId, this.editorController.captions.parameters, this.editorController.etag, this.editorController.label, this.editorController.language);
    }
    import(trsx, shouldAppend = false, importFromTime = 0, removeCaptions = false, ignoreHeadings = false) {
        this.etag = trsx.etag;
        const parsedTrsx = parseTrsx(trsx.content, importFromTime);
        if (!parsedTrsx.success) {
            throw Error(parsedTrsx.error);
        }
        this.importFromTime = importFromTime;
        this.currentEndIndex = shouldAppend ? this.editorController.getLength() : 0;
        this.setEditorModules(parsedTrsx.value.areSummariesPresent(), shouldAppend);
        this.saveGeneralTrsxMetadata(parsedTrsx.value.getGeneralMetadata());
        this.importSpeakers(parsedTrsx.value.getSpeakers());
        this.setCaptionMetadata(parsedTrsx.value.getCaptionsMeta(), removeCaptions ? 'hidden' : 'visible');
        this.generateDelta(parsedTrsx.value.getChapters(), shouldAppend, ignoreHeadings);
        this.cleanEditorAfterDeltaInsert();
    }
    setCaptionMetadata(trsxCaptionsMeta, captions) {
        this.captionMetadata = captions === 'visible' && trsxCaptionsMeta !== undefined
            ? this.translateCaptionsMetaToParams(trsxCaptionsMeta)
            : null;
    }
    setEditorModules(showSummaries, shouldAppend) {
        if (showSummaries) {
            this.editorController.summary.setEnabled(true);
        }
        if (!shouldAppend) {
            this.editorController.textMetadata = new TextMetadata();
            this.editorController.speakers.setDocumentSpeakers([]);
            this.editorController.speakers.speakerIdentification.reset();
        }
    }
    cleanEditorAfterDeltaInsert() {
        this.editorController.textMetadata.length = this.editorController.getLength();
        this.editorController.getHistory().clear();
    }
    insertEmptyLineForEditsBeforeSpeaker() {
        if (this.editorController.getLineFormat(0).speaker !== undefined) {
            const emptyLineDelta = new Delta({ ops: [{ insert: '\n' }] });
            this.editorController.updateContents(emptyLineDelta, 'user');
            // NOTE: This must be handled explicitly, because import is being run with
            // automatic timestamps updates disabled
            this.editorController.textMetadata.applyDelta(emptyLineDelta);
        }
    }
    generateDelta(chapters, shouldAppend, ignoreHeadings) {
        let delta = new Delta();
        if (shouldAppend) {
            // NOTE: Final line end will be replaced by space.
            delta = delta
                .retain(this.editorController.getLength() - 1)
                .delete(1)
                .insert(' ');
            this.isLastParagraphTerminated = false;
        }
        for (let i = 0; i < chapters.length; i += 1) {
            delta = delta.concat(this.generateChapterDelta(chapters[i], ignoreHeadings));
        }
        if (shouldAppend) {
            this.editorController.updateContents(delta, 'user');
        }
        else {
            if (delta.length() === 0) {
                // NOTE: Document must end with a new line, even empty document.
                delta.push({ insert: '\n' });
            }
            this.setQuillContents(delta);
        }
        this.insertEmptyLineForEditsBeforeSpeaker();
        if (shouldAppend) {
            this.isLastParagraphTerminated = true;
        }
    }
    generateChapterDelta(chapter, ignoreHeadings) {
        let delta = new Delta();
        if (chapter.name !== '' && !ignoreHeadings) {
            delta.push({
                insert: `${chapter.name}\n`,
                attributes: { header: 1 },
            });
            this.currentEndIndex += chapter.name.length + 1;
        }
        for (let i = 0; i < chapter.sections.length; i += 1) {
            delta = delta.concat(this.generateSectionDelta(chapter.sections[i], ignoreHeadings));
        }
        return delta;
    }
    generateSectionDelta(section, ignoreHeadings) {
        this.lastSpeakerId = null;
        let delta = new Delta();
        const { name, summary, tags, id, paragraphs, } = section;
        const shouldGenerateHeading = !ignoreHeadings && (name !== '' || summary !== '\\n');
        if (shouldGenerateHeading) {
            const isSectionNameMissing = this.editorController.sectionNamesList !== null
                && !this.editorController.sectionNamesList.options.some((option) => option.name === name);
            delta.push({
                insert: `${name}\n`,
                attributes: {
                    section: {
                        id: id === undefined || id === '' ? null : id,
                        tags,
                    },
                    uneditable: this.editorController.sectionNamesList !== null,
                    warning: isSectionNameMissing ? 'section-name-missing' : undefined,
                },
            });
            this.currentEndIndex += name.length + 1;
            if (this.editorController.summary.getEnabled()) {
                delta = delta.concat(this.generateSummaryDelta(summary));
            }
        }
        for (let i = 0; i < paragraphs.length; i += 1) {
            delta = delta.concat(this.generateParagraphDelta(paragraphs[i]));
        }
        return delta;
    }
    generateSummaryDelta(summary) {
        const delta = new Delta();
        const summaryBlocks = summary.split('\\n').slice(0, -1);
        summaryBlocks.forEach((summaryBlockText) => {
            delta.push({
                insert: `${summaryBlockText}\n`,
                attributes: { summary: 'button' },
            });
            this.currentEndIndex += summaryBlockText.length + 1;
        });
        return delta;
    }
    saveGeneralTrsxMetadata(metadata) {
        var _a, _b, _c, _d, _e;
        this.unmaintainedMetadata = metadata.unmaintainedSubElements;
        this.lastEditorUsername = (_a = metadata.lastEditorUserName) !== null && _a !== void 0 ? _a : null;
        this.lastPlaybackTime = (_b = metadata.lastPlaybackPosition) !== null && _b !== void 0 ? _b : null;
        this.trsxCreatedTime = (_c = metadata.trsxCreatedTime) !== null && _c !== void 0 ? _c : null;
        this.editorController.lastHandledMessageId = (_d = metadata.lastMessageId) !== null && _d !== void 0 ? _d : Infinity;
        this.editorController.lastHandledPhraseTime = (_e = metadata.lastPhraseTime) !== null && _e !== void 0 ? _e : Infinity;
    }
    generateParagraphDelta(paragraph) {
        let delta = new Delta();
        const { phrases, display } = paragraph;
        if (paragraph.end < this.importFromTime) {
            return new Delta();
        }
        if (phrases.length === 0 && display === 'hidden') {
            return new Delta();
        }
        this.appendSpeaker(paragraph, delta);
        for (let i = 0; i < phrases.length; i += 1) {
            const isLastWord = i === phrases.length - 1;
            delta = delta.concat(this.generateWordDelta(phrases[i], isLastWord));
        }
        delta.push({
            insert: '\n',
        });
        this.currentEndIndex += 1;
        return delta;
    }
    appendSpeaker(paragraph, delta) {
        const { speakerId } = paragraph;
        let speaker = this.trsxSpeakers[speakerId];
        // NOTE: Create unknown speaker number only for speakers that are really added to
        // the document (do not number empty paragraphs).
        if (speaker !== undefined && this.shouldInsertSpeaker(paragraph)) {
            if (isRawDiarizationCode(speaker.lastName)) {
                speaker = this.editorController.speakers
                    .speakerIdentification.createSpeaker(speaker.lastName);
                this.trsxSpeakers[speakerId] = speaker;
            }
            this.editorController.speakers.speakerIdentification.updateSpeakerCounter(speaker);
            this.tryAddToDocumentSpeakers(speaker);
            const label = speaker.composeLabel();
            if (paragraph.notForTraining !== undefined) {
                delta.push({
                    insert: `${label}`,
                    attributes: { notForTraining: paragraph.notForTraining },
                });
            }
            else {
                delta.push({ insert: `${label}` });
            }
            // NOTE: the speaker must be applied to the newline only,
            // that means format the whole line.
            delta.push({ attributes: { speaker: speaker.getQuillFormatValue() }, insert: '\n' });
            this.currentEndIndex += label.length + 1;
        }
        this.lastSpeakerId = speakerId;
    }
    generateWordDelta(phrase, isLastWord) {
        var _a, _b, _c, _d;
        let delta = new Delta();
        if (((_a = phrase.attributes.captions) === null || _a === void 0 ? void 0 : _a.type) === 'extraText') {
            return this.generateSpeakerSignDelta(phrase);
        }
        // NOTE: Must be here, time stamp precedes the word.
        if (phrase.attributes.displayTimeStamp === true) {
            delta = delta.concat(this.generateTimeStampMarkerDelta(phrase.begin));
        }
        if (phrase.text.length > 0) {
            delta.push({
                insert: phrase.text,
                attributes: {
                    speaker: false,
                    timeStampMarker: false,
                },
            });
            this.currentEndIndex += phrase.text.length;
            this.editorController.textMetadata.addTimestampAtEndOnIndex(isLastWord ? this.currentEndIndex : this.currentEndIndex - 1, phrase.begin, phrase.end);
        }
        if (((_b = phrase.attributes.captions) === null || _b === void 0 ? void 0 : _b.type) === 'captionEnd' && this.captionMetadata) {
            delta = delta.concat(this.generateCaptionEndDelta(phrase.attributes.captions.captionEndTime));
        }
        if (phrase.attributes.timeAnchor !== undefined) {
            delta = delta.concat(this.generateTimeAnchorDelta(phrase.attributes.timeAnchor));
        }
        if (((_c = phrase.attributes.captions) === null || _c === void 0 ? void 0 : _c.type) === 'lineBreak' && this.captionMetadata) {
            this.addCaptionLineBreak(isLastWord);
        }
        if (((_d = phrase.attributes.captions) === null || _d === void 0 ? void 0 : _d.type) === 'captionEnd'
            && phrase.attributes.captions.captionPosition !== undefined && this.captionMetadata) {
            this.addCaptionPosition(phrase.attributes.captions.captionPosition);
        }
        return delta;
    }
    generateTimeAnchorDelta(value) {
        const delta = new Delta([{
                insert: TIME_ANCHOR_SYMBOL,
                attributes: {
                    speaker: false,
                    timeAnchor: value,
                },
            }]);
        this.currentEndIndex += 1;
        this.editorController.textMetadata.addTimestampAtEndOnIndex(this.currentEndIndex - 1, value, value);
        this.editorController.textMetadata.addMetadata('timeAnchor', this.currentEndIndex - 1, value);
        return delta;
    }
    generateTimeStampMarkerDelta(time) {
        const timeStampMarker = formatTimeStampMarker(time);
        const delta = new Delta([{
                insert: timeStampMarker,
                attributes: {
                    timeStampMarker: true,
                },
            }]);
        this.currentEndIndex += timeStampMarker.length;
        this.editorController.textMetadata.addMetadata('displayTimeStamp', this.currentEndIndex - 1, true);
        return delta;
    }
    generateCaptionEndDelta(value) {
        const delta = new Delta([{
                insert: CAPTION_END_SYMBOL,
                attributes: {
                    speaker: false,
                },
            }]);
        this.currentEndIndex += 1;
        this.editorController.textMetadata.addMetadata('captionEnd', this.currentEndIndex - 1, value);
        return delta;
    }
    generateSpeakerSignDelta(phrase) {
        var _a;
        const delta = new Delta();
        // NOTE: Speaker signs are only relevant for captions.
        if (((_a = phrase.attributes.captions) === null || _a === void 0 ? void 0 : _a.type) !== 'extraText' || this.captionMetadata === null) {
            return delta;
        }
        const display = phrase.text.length > 0 ? 'show' : 'hide';
        this.editorController.textMetadata.addMetadata('speakerSign', this.currentEndIndex - 1, {
            content: phrase.text,
            display: phrase.attributes.captions.overrideSpeakerDisplay === true ? display : 'default',
        });
        return delta;
    }
    addCaptionLineBreak(isAtParagraphEnd) {
        const index = isAtParagraphEnd ? this.currentEndIndex : this.currentEndIndex - 1;
        this.editorController.textMetadata.addMetadata('captionLineBreak', index, true);
    }
    addCaptionPosition(position) {
        const { horizontal, vertical } = position;
        const positionString = `align:${horizontal} line:${vertical}%`;
        this.editorController.textMetadata.addMetadata('captionFormat', this.currentEndIndex - 1, positionString);
    }
    importSpeakers(speakerElements) {
        this.trsxSpeakers = {};
        for (let i = 0; i < speakerElements.length; i += 1) {
            const trsxSpeaker = speakerElements[i];
            const speaker = Speaker.fromTrsxSpeaker(trsxSpeaker);
            const trsxId = trsxSpeaker.id;
            this.trsxSpeakers[trsxId] = speaker;
        }
    }
    /* NOTE: The function setContents from quill API behaves unpredictably
    - sometimes it adds additional newline at end and sometimes not.
    This function does the same thing as quill.setContents,
    but more reliably.
    */
    setQuillContents(delta) {
        this.editorController.deleteText(0, this.editorController.getLength(), 'user');
        // after deleting, there is only a newline in the editor
        this.editorController.updateContents(delta, 'user');
        // now there is the new content and after that there is the newline, that was there before
        this.editorController.deleteText(this.editorController.getLength() - 1, 1);
        // now the additional newline is deleted and contents are exactly the inserted delta
    }
    // NOTE: Will be removed later when we switched to new Captions structure.
    translateCaptionsMetaToParams(captionsMeta) {
        var _a, _b, _c, _d, _e, _f;
        const { display, settings } = captionsMeta;
        const position = {
            line: display.style.position.vertical,
            align: display.style.position.horizontal,
        };
        return {
            autofillPauseBetweenCaptions: display.timing.autoGapFill,
            automaticSpeed: display.timing.automaticSpeed,
            defaultBackgroundColor: display.style.background.fill,
            defaultBackgroundTransparency: this.changeToTransparency(String(display.style.background.transparency), defaultCaptionParameters.defaultBackgroundTransparency),
            defaultCaptionPosition: position,
            defaultColor: display.style.text.fill,
            defaultFontName: display.style.text.fontFamily,
            defaultFontSize: display.style.text.fontSize,
            enablePresegmentation: settings.presegmented,
            keepInnerLinesStripped: display.contentLayout.trimLines,
            maxDuration: display.timing.maxDuration,
            minDuration: display.timing.minDuration,
            maxLineLength: settings.maxLineLength,
            pauseBetweenCaptions: display.timing.gap,
            speedCriticalWarning: settings.speedCriticalWarningLimit,
            speedWarning: settings.speedWarningLimit,
            subtitlerMaxLineCount: settings.maxLineCount,
            upperCaseAllText: display.style.text.upperCase,
            useSpeakerName: display.contentLayout.speakers.speakerSign === 'speakerName',
            speakerSignPlacement: display.contentLayout.speakers.speakerSignPlacement,
            speakerSign: '-',
            templateName: display.template,
            highlightingMode: (_c = (_b = (_a = display.animation) === null || _a === void 0 ? void 0 : _a.legacyHighlight) === null || _b === void 0 ? void 0 : _b.mode) !== null && _c !== void 0 ? _c : defaultCaptionParameters.highlightingMode,
            unHighlightedColor: (_f = (_e = (_d = display.animation) === null || _d === void 0 ? void 0 : _d.legacyHighlight) === null || _e === void 0 ? void 0 : _e.unhighlightedColor) !== null && _f !== void 0 ? _f : defaultCaptionParameters.unHighlightedColor,
        };
    }
}
